import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { settingsApi } from '@/features/settings/api/settingsApi';
import { settingsKeys } from '@/features/settings/hooks/useSettings';
import type { FeatureFlag } from '@/features/settings/types';
import { useAuth } from '@/providers/AuthProvider';

interface ProductFeaturesContextValue {
  flags: FeatureFlag[];
  isLoading: boolean;
}

const ProductFeaturesContext = createContext<ProductFeaturesContextValue>({
  flags: [],
  isLoading: true,
});

export function ProductFeaturesProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: settingsKeys.features(),
    queryFn: () => settingsApi.getFeatures(),
    staleTime: 30_000,
    retry: 1,
    enabled: isAuthenticated,
  });

  const streamingEnabled = data?.some((flag) => flag.id === 'laminar' && flag.enabled) ?? false;
  const runtime = useQuery({
    queryKey: ['laminar', 'runtime-capabilities'],
    queryFn: async () => {
      const response = await fetch('/laminar/streaming/runtime-capabilities', {
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Unable to check Jobs availability');
      return response.json() as Promise<{ unified: boolean }>;
    },
    enabled: isAuthenticated && streamingEnabled,
    staleTime: 30_000,
    retry: 1,
  });
  const jobsEnabled = streamingEnabled && runtime.data?.unified === true;
  const featuresLoading = isLoading || (streamingEnabled && runtime.isLoading);
  const value = useMemo<ProductFeaturesContextValue>(
    () => ({
      flags: [
        ...(data ?? []),
        {
          id: 'laminar-jobs',
          title: 'Jobs',
          description: 'Unified workloads and workbench',
          enabled: jobsEnabled,
        },
      ],
      isLoading: featuresLoading,
    }),
    [data, jobsEnabled, featuresLoading]
  );

  return (
    <ProductFeaturesContext.Provider value={value}>{children}</ProductFeaturesContext.Provider>
  );
}

/** Check a single feature flag */
export function useProductFeature(flagId: string) {
  const { flags, isLoading } = useContext(ProductFeaturesContext);
  const flag = flags.find((f) => f.id === flagId);
  // status comes back on the same payload and answers a different question from
  // enabled: whether the product works, rather than whether it is switched on.
  return { enabled: flag?.enabled ?? false, status: flag?.status, isLoading };
}

/** Access all feature flags */
export function useProductFeatures() {
  const { flags, isLoading } = useContext(ProductFeaturesContext);
  const isFeatureEnabled = (flagId: string) => flags.find((f) => f.id === flagId)?.enabled ?? false;
  return { isFeatureEnabled, isLoading };
}
